// The camera step that sits above Clock In.
//
// Two modes in one component, because they are the same three things (open a
// camera, show live framing feedback, commit frames) and splitting them would
// mean maintaining that twice:
//
//   enroll — first time only. Consent, then N good frames become the templates.
//   verify — every punch after that. One frame; on success it clocks in.
//
// The frames never touch localStorage or Redux and are dropped when the modal
// closes. What persists lives in face-service, encrypted, and only ever as a
// vector — never the image.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ShieldCheck, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  checkFrame,
  enrollFace,
  faceClockIn,
  grantFaceConsent,
  readFaceFailure,
  type FaceStatus,
} from '../../api/face';
// The same fix the ordinary Clock In takes — the server geofences either way.
// This app's own watch-based helper, not teamlio's single-reading one: the
// first fix a browser returns is routinely a cached network estimate accurate
// to a kilometre, which fails the geofence on the first punch of a session.
import { requestBrowserCoordinates } from '../../api/attendance';
import {
  cameraLabel,
  chooseCamera,
  readStoredCamera,
  storeCamera,
  videoInputs,
} from './faceCamera';

/** Capture size. Was 640x480 — the size face-service's minimum eye distance
 *  was tuned against — which put the pass line at about arm's length. Doubling
 *  the frame doubles the eye distance at the same physical distance, so the
 *  same threshold is met from roughly twice as far. Detection cost is unchanged
 *  (SCRFD letterboxes to 640 regardless); the JPEG is ~3x larger.
 *
 *  16:9, not 4:3: 1280x720 is a NATIVE mode on every 720p/1080p webcam and
 *  phone. 1280x960 is not — a 1080p laptop camera synthesised it by upscaling
 *  and every frame failed the sharpness gate. Ask for what the sensor can do.
 *  Same numbers as hrpayroll's FaceCheckModal and the mobile WebView page;
 *  keep the four in step. */
const CAPTURE_W = 1280;
const CAPTURE_H = 720;

// How many good frames an enrolment collects. The server decides how many it
// KEEPS; this is only how many the wizard asks for.
const ENROLL_FRAMES = 4;
// Live feedback cadence. Every frame would be a request per 16ms; ~2/s is
// responsive enough to guide someone and leaves the CPU for the punch itself.
const FEEDBACK_MS = 500;

type Mode = 'enroll' | 'verify';

/** Supervised capture: an operator enrolling somebody ELSE who is standing at
 *  this camera, at a shared kiosk.
 *
 *  Passing this turns the enrol flow around. Self-service enrolment asks the
 *  person in front of the camera to agree and then captures them; here the
 *  person agreeing and the person being captured are different people, so the
 *  subject is named and pictured throughout — an operator must never be able to
 *  enrol one worker believing they are another — and the consent copy is
 *  addressed to the operator to read aloud rather than to the reader.
 *
 *  `enroll` is supplied by the caller rather than called from here, because the
 *  supervised endpoint is kiosk-scoped and this component has no business
 *  knowing about kiosks. */
export interface SupervisedSubject {
  name: string;
  photoUrl?: string | null;
  employeeCode?: string | null;
  enroll: (imagesB64: string[]) => Promise<{
    ok: boolean;
    accepted_frames: number;
    message?: string;
  }>;
}

interface Props {
  open: boolean;
  mode: Mode;
  status: FaceStatus | null;
  /** Present => supervised capture of somebody else. Absent => self-service. */
  subject?: SupervisedSubject;
  /** Passed through to the clock-in so the punch records where they worked. */
  workfromhome?: boolean;
  onClose: () => void;
  /** Verification succeeded AND the punch was recorded. */
  onClockedIn: (result: any) => void;
  /** Enrolment finished — the caller re-reads status and usually reopens in
   *  verify mode so the user can actually clock in. */
  onEnrolled: () => void;
}

export default function FaceCheckModal({
  open, mode, status, subject, workfromhome, onClose, onClockedIn, onEnrolled,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const feedbackTimer = useRef<number | null>(null);
  // A ref, not state: the capture loop reads it between awaits and would
  // otherwise see a stale value from the render it started in.
  const busyRef = useRef(false);
  // Guards the auto-start so the run happens once per open, not once per
  // render — and not twice under React StrictMode's double-invoked effects.
  const startedRef = useRef(false);
  // Set on close so a loop still mid-await abandons instead of finishing
  // against a modal that is gone.
  const cancelledRef = useRef(false);

  const [cameraError, setCameraError] = useState<string | null>(null);
  // Every camera the browser can see, and the one the stream is actually on.
  // Drives the picker and the "no face from <camera>" hint — see faceCamera.ts
  // for why the default device is not to be trusted on a desktop.
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  // Consecutive feedback ticks that found no face. Past a few of them the hint
  // names the camera, because a placeholder feed from a virtual camera looks
  // exactly like "no face" and the generic advice ("check the lighting") sends
  // people the wrong way.
  const noFaceStreak = useRef(0);
  const [hint, setHint] = useState<string>('Getting the camera ready…');
  const [frameOk, setFrameOk] = useState(false);
  const [captured, setCaptured] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [consented, setConsented] = useState(false);

  // In supervised mode consent is recorded by hrms as part of the enrolment,
  // under a distinct method — there is no self-consent endpoint to call for
  // somebody else. The panel is still shown, because the operator reading it
  // aloud to the worker IS the consent moment; `consented` just gates the
  // camera behind it.
  const needsConsent = mode === 'enroll' && !consented
    && (subject ? true : !status?.consent);

  // ---- camera -------------------------------------------------------------

  const stopCamera = useCallback(() => {
    if (feedbackTimer.current) {
      window.clearTimeout(feedbackTimer.current);
      feedbackTimer.current = null;
    }
    // Release the device explicitly. Without this the browser keeps the camera
    // light on after the modal closes, which reads to the user as "it is still
    // watching me" — and for a face feature that impression matters. In the
    // desktop build it matters more: the window outlives the page.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  /** Resolves once the camera is actually producing frames — not merely open.
   *
   *  Auto-exposure and focus settle over the first second or so, and a device
   *  that was just released takes longer still to come back. Waiting on a fixed
   *  timer meant the first frames went out dark and were rejected as blurry,
   *  which reads to the user as "it can't see me" when the truth is "it hasn't
   *  woken up yet". */
  const waitForVideo = useCallback(async (ms = 4000): Promise<boolean> => {
    const deadline = Date.now() + ms;
    while (Date.now() < deadline) {
      const v = videoRef.current;
      if (v && v.readyState >= 2 && v.videoWidth > 0) {
        // A short settle AFTER the first frame: the earliest frames are the
        // badly-exposed ones.
        await new Promise((r) => setTimeout(r, 600));
        return true;
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }, []);

  const startCamera = useCallback(async (preferredId?: string | null) => {
    setCameraError(null);
    noFaceStreak.current = 0;
    const size = { width: { ideal: CAPTURE_W }, height: { ideal: CAPTURE_H } };
    const enumerate = async () => {
      try {
        return videoInputs(await navigator.mediaDevices.enumerateDevices());
      } catch {
        return [] as MediaDeviceInfo[];
      }
    };
    const open = (deviceId: string | null) =>
      navigator.mediaDevices.getUserMedia({
        video: deviceId ? { ...size, deviceId: { exact: deviceId } } : { ...size, facingMode: 'user' },
        audio: false,
      });
    try {
      // 1. Decide from what is visible now. Before the first permission grant
      //    labels are blank and chooseCamera() abstains -> browser default.
      let cams = await enumerate();
      let wanted = preferredId ?? chooseCamera(cams, readStoredCamera())?.deviceId ?? null;
      let stream: MediaStream;
      try {
        stream = await open(wanted);
      } catch (err) {
        // A remembered / preferred camera that is no longer there must not
        // turn into an error screen: fall back to the browser's choice.
        const name = (err as { name?: string } | null)?.name || '';
        if (wanted && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
          storeCamera(null);
          wanted = null;
          stream = await open(null);
        } else {
          throw err;
        }
      }
      // 2. Labels are readable now. If the default the browser opened is a
      //    virtual camera and a physical one exists, switch — once.
      let active = stream.getVideoTracks()[0]?.getSettings?.().deviceId || wanted;
      cams = await enumerate();
      if (!wanted) {
        const better = chooseCamera(cams, null);
        if (better && active && better.deviceId !== active) {
          stream.getTracks().forEach((t) => t.stop());
          stream = await open(better.deviceId);
          active = better.deviceId;
        }
      }
      streamRef.current = stream;
      setDevices(cams);
      setActiveDeviceId(active || null);
      // Which size the browser ACTUALLY gave us — `ideal` is a hint. Logged so a
      // distance complaint can be traced to a camera that silently stayed at 640.
      const settings = stream.getVideoTracks()[0]?.getSettings?.();
      const label = cameraLabel(cams.find((d) => d.deviceId === active), 0);
      if (settings?.width) console.info(`[face] camera stream ${settings.width}x${settings.height} (${label})`);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { /* autoplay guard */ });
      }
    } catch (err: any) {
      // Name the actual cause. Each of these has a completely different fix,
      // and "camera failed" sends someone to IT when the answer is a dialog
      // they dismissed or an app they left open.
      const name = err?.name || '';
      let message: string;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        message = 'Camera access was blocked. Allow it in your browser settings, then try again.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
        // The device exists but something else holds it. Windows gives the
        // camera to one application at a time, so any other window with it
        // open — Teams, Zoom, a script — locks the browser out entirely.
        message = 'Another app is using the camera. Close it (Teams, Zoom, a camera window) and try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        message = 'No camera found. Plug one in, or ask HR to clock you in.';
      } else if (name === 'OverconstrainedError') {
        message = "This camera doesn't support the required settings. Try a different one.";
      } else {
        message = `The camera couldn't start (${name || 'unknown error'}). Try again, or ask HR to clock you in.`;
      }
      setCameraError(message);
    }
  }, []);

  useEffect(() => {
    if (!open || needsConsent) return;
    startCamera();
    return stopCamera;
  }, [open, needsConsent, startCamera, stopCamera]);

  const switchCamera = useCallback((deviceId: string) => {
    storeCamera(deviceId);
    setFailure(null);
    setFrameOk(false);
    setHint('Switching camera…');
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    startCamera(deviceId);
  }, [startCamera]);

  // ---- frames -------------------------------------------------------------

  const grabFrame = useCallback((): string | null => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    // readyState, not just videoWidth: a <video> reports its dimensions as soon
    // as metadata arrives, several hundred ms before any pixels exist. Capturing
    // in that window yields a black or half-exposed frame, which the server
    // then — correctly — calls blurry. HAVE_CURRENT_DATA (2) means there is an
    // actual frame to draw.
    if (!video || !canvas || !video.videoWidth || video.readyState < 2) return null;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Strip the data: prefix — the API wants bare base64.
    return canvas.toDataURL('image/jpeg', 0.92).split(',')[1] || null;
  }, []);

  // Live "move closer / hold still" feedback while idle.
  useEffect(() => {
    // Stop once a failure is on screen. The loop writes an encouraging green
    // "Looking good — hold still." into the hint every tick, which sits
    // directly above the red explanation of why the punch was refused and
    // reads as the louder of the two — so the user waits for a success that
    // is never coming instead of reading the error. retry() clears failure,
    // which restarts this effect.
    if (!open || needsConsent || cameraError || failure) return;
    // Self-scheduling rather than setInterval: a check is CPU-bound inference
    // on the server and regularly takes longer than FEEDBACK_MS. setInterval
    // fires on a fixed schedule regardless, so every tick that overruns leaves
    // its request in flight while the next one starts — the overlap compounds
    // until a handful of requests are queued behind each other, latency climbs
    // past the caller's connect timeout, and the whole feature 503s under what
    // is really self-inflicted load. Waiting FEEDBACK_MS *after* each result
    // keeps exactly one feedback request outstanding at any moment.
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (!busyRef.current) {           // never compete with a capture
        const frame = grabFrame();
        if (frame) {
          try {
            const res = await checkFrame(frame, mode === 'enroll');
            if (!cancelled) {
              setFrameOk(res.ok);
              noFaceStreak.current = res.outcome === 'no_face' ? noFaceStreak.current + 1 : 0;
              setHint(res.ok ? 'Looking good — hold still.' : res.message);
            }
          } catch {
            // A dropped feedback tick is not worth a message; the commit path
            // reports properly if the service is really down.
          }
        }
      }
      // cancelledRef too, not just the effect-local flag: close() sets it and
      // clears the pending timeout, but a tick already awaiting a response
      // would otherwise reschedule itself after the camera has been released.
      if (cancelled || cancelledRef.current) return;
      feedbackTimer.current = window.setTimeout(tick, FEEDBACK_MS);
    };

    feedbackTimer.current = window.setTimeout(tick, FEEDBACK_MS);
    return () => {
      cancelled = true;
      if (feedbackTimer.current) window.clearTimeout(feedbackTimer.current);
      feedbackTimer.current = null;
    };
  }, [open, needsConsent, cameraError, failure, grabFrame, mode]);

  // ---- actions ------------------------------------------------------------

  const doConsent = async () => {
    if (subject) {
      // Nothing to call: hrms writes the consent row alongside the templates,
      // so that a face can never exist without one. Recording it here would
      // leave a consent with no enrolment behind every abandoned capture.
      setConsented(true);
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      await grantFaceConsent();
      setConsented(true);
    } catch (err) {
      setFailure(readFaceFailure(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  // Set when a frame check fails as a REQUEST rather than as a verdict, so the
  // caller can say "we couldn't reach the server" instead of blaming the user's
  // lighting. hrms being down looked exactly like a dark room: the loop bailed
  // on the first thrown call and reported "couldn't get a clear view".
  const transportErrorRef = useRef<string | null>(null);

  const waitForGoodFrame = async (strict: boolean): Promise<string | null> => {
    transportErrorRef.current = null;
    // Up to ~15s of trying. The frame is checked BEFORE it is committed: a
    // blurry enrolment frame degrades every future match for this person, and
    // unlike a bad verification frame it does not go away on retry.
    for (let attempt = 0; attempt < 30; attempt++) {
      if (cancelledRef.current) return null;
      const frame = grabFrame();
      if (frame) {
        try {
          const res = await checkFrame(frame, strict);
          setFrameOk(res.ok);
          setHint(res.ok ? 'Hold still…' : res.message);
          if (res.ok) return frame;
        } catch (err) {
          // Give up immediately — retrying a frame check against a service
          // that isn't answering just burns 15 seconds before the same result.
          transportErrorRef.current = readFaceFailure(err).message
            || "Couldn't reach the check-in service.";
          return null;
        }
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    return null;
  };

  const doEnroll = async () => {
    busyRef.current = true;
    setSubmitting(true);
    setFailure(null);
    setCaptured(0);
    try {
      const frames: string[] = [];
      for (let i = 0; i < ENROLL_FRAMES; i++) {
        setHint(`Frame ${i + 1} of ${ENROLL_FRAMES} — turn your head slightly.`);
        const frame = await waitForGoodFrame(true);
        if (cancelledRef.current) return;
        if (!frame) {
          setFailure(
            transportErrorRef.current
              || "Couldn't get a clear frame. Try better lighting, facing the camera.",
          );
          return;
        }
        frames.push(frame);
        setCaptured(i + 1);
        // A beat between frames so the templates are not four copies of one
        // instant — the pose variation is the point of taking several.
        await new Promise((r) => setTimeout(r, 600));
      }
      const result = subject ? await subject.enroll(frames) : await enrollFace(frames);
      if (!result.ok || result.accepted_frames === 0) {
        setFailure(result.message || "None of the frames were usable.");
        return;
      }
      onEnrolled();
    } catch (err) {
      setFailure(readFaceFailure(err).message);
    } finally {
      busyRef.current = false;
      setSubmitting(false);
      // The camera is NOT stopped here. On success the modal closes and the
      // effect cleanup releases it; on failure the user is still sitting in
      // front of it about to press Try again, and re-acquiring a just-released
      // device is the slowest path there is — it produced exactly the dark
      // frames that caused the failure in the first place.
    }
  };

  const doVerify = async () => {
    busyRef.current = true;
    setSubmitting(true);
    setFailure(null);
    try {
      // Start the GPS fix and the frame wait TOGETHER. Measured sequentially
      // (frame wait, then location) these summed to ~17s in a real trace —
      // ~8s waiting for a good frame, then a further ~9s for
      // watchPosition to settle, back to back, because nothing needed the
      // location before now. Started here, it settles in the background
      // while the person is still positioning their face, and only the
      // slower of the two ends up dictating the wait instead of both.
      // undefined (not started) for WFH, which never needs a fix at all —
      // starting it anyway would fire a location permission prompt nobody
      // asked for.
      const locationPromise = workfromhome ? undefined : requestBrowserCoordinates();
      // Cancelling the modal while this is still settling must not surface as
      // an unhandled rejection — the real handling below only runs if we get
      // past the frame wait without being cancelled.
      locationPromise?.catch(() => {});

      // Wait for a frame the service would accept rather than firing the first
      // one off the camera: a rejected punch costs a round trip and reads to
      // the user as a failure, when it was only bad timing.
      const frame = await waitForGoodFrame(false);
      if (cancelledRef.current) return;
      if (!frame) {
        setFailure(
          transportErrorRef.current
            || "Couldn't get a clear view — check the lighting and try again.",
        );
        return;
      }
      setHint('Checking…');

      // The face proves WHO. The geofence proves WHERE — and the server checks
      // both. Sending no location made every in-office punch fail the geofence
      // while the UI reported "clocked in, face verified", because the refusal
      // comes back as a field rather than an error.
      let latitude: string | null = null;
      let longitude: string | null = null;
      let accuracy: number | null = null;
      if (locationPromise) {
        try {
          const fix = await locationPromise;
          latitude = String(fix.latitude);
          longitude = String(fix.longitude);
          accuracy = fix.accuracy ?? null;
        } catch {
          setFailure(
            'Location is needed to clock in from the office. Allow location access and try again.',
          );
          return;
        }
      }

      const result = await faceClockIn(frame, {
        workfromhome, latitude, longitude, accuracy,
      });
      onClockedIn(result);
    } catch (err) {
      const fail = readFaceFailure(err);
      // not_enrolled means their templates went away (erased, or a fresh
      // account) — send them to the wizard rather than telling them their own
      // face was rejected.
      if (fail.outcome === 'not_enrolled') {
        onEnrolled();
        return;
      }
      setFailure(fail.message);
    } finally {
      busyRef.current = false;
      setSubmitting(false);
      // See doEnroll: released on close, not on failure.
    }
  };

  // A mode change is a new run, even though the modal never closed. Enrolment
  // hands straight over to verification — the same Clock In press has to finish
  // the punch — so `open` stays true across the switch and the reset-on-close
  // effect below never fires. Without clearing startedRef here the auto-start
  // effect sees the enrolment's own flag still set and returns early, leaving
  // the camera live and the spinner turning with nothing driving it.
  // Declared before the auto-start effect so it runs first on the same commit.
  useEffect(() => {
    startedRef.current = false;
    setCaptured(0);
    setFailure(null);
  }, [mode]);

  // Start on its own once the camera is live. The user already asked for this
  // by pressing Clock In — making them press a second button to take a photo
  // adds a step that answers no question, and it is the step most likely to be
  // pressed at the wrong moment, before the framing settles.
  useEffect(() => {
    if (!open || needsConsent || cameraError || startedRef.current) return;
    startedRef.current = true;
    cancelledRef.current = false;
    (async () => {
      setHint('Waking the camera…');
      const ready = await waitForVideo();
      if (cancelledRef.current) return;
      if (!ready) {
        // Name the real problem. "Couldn't get a clear view" sends someone to
        // fix the lighting when the camera never started.
        setFailure("The camera didn't start. Close this, check nothing else is using it, and try again.");
        return;
      }
      if (mode === 'enroll') void doEnroll();
      else void doVerify();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsConsent, cameraError, mode]);

  // Reset between opens, so the next Clock In starts a fresh run rather than
  // showing the last one's outcome.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      cancelledRef.current = true;
      setCaptured(0);
      setFailure(null);
      setFrameOk(false);
      setHint('Getting the camera ready…');
    }
  }, [open]);

  const retry = async () => {
    setFailure(null);
    setCaptured(0);
    setHint('Waking the camera…');
    cancelledRef.current = false;
    // Usually still streaming (a failed run no longer releases it), so this is
    // a no-op; it only re-opens when the camera really did drop.
    if (!streamRef.current) await startCamera();
    const ready = await waitForVideo();
    if (cancelledRef.current) return;
    if (!ready) {
      setFailure("The camera didn't start. Close this, check nothing else is using it, and try again.");
      return;
    }
    if (mode === 'enroll') void doEnroll();
    else void doVerify();
  };

  // ---- render -------------------------------------------------------------

  const close = () => {
    // Tell any in-flight loop to stop before the camera goes away.
    cancelledRef.current = true;
    stopCamera();
    setFailure(null);
    setCaptured(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {subject
              ? `Set up face check-in for ${subject.name}`
              : mode === 'enroll' ? 'Set up face check-in' : 'Face check-in'}
          </DialogTitle>
        </DialogHeader>

        {/* WHO IS BEING RECORDED, on screen for the whole capture.
            In supervised mode the person operating the camera is not the person
            in front of it, so this is the only thing standing between "enrol
            Meera" and "enrol whoever happened to walk up". It stays visible
            through consent, capture and failure — not just on the first step. */}
        {subject && (
          <div
            className="mb-3 flex items-center gap-3 rounded-md border p-2"
            style={{ borderColor: 'var(--gt-divider)', background: 'var(--gt-surface-muted)' }}
          >
            {subject.photoUrl ? (
              <img
                alt=""
                className="h-10 w-10 rounded-full object-cover"
                src={subject.photoUrl}
              />
            ) : (
              <span
                aria-hidden
                className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
                style={{ background: 'var(--gt-surface)', color: 'var(--gt-text-secondary)' }}
              >
                {subject.name.slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <div className="truncate font-semibold" data-testid="supervised-subject">
                {subject.name}
              </div>
              <div className="text-xs" style={{ color: 'var(--gt-text-tertiary)' }}>
                {subject.employeeCode ? `${subject.employeeCode} — ` : ''}
                must be at this camera now
              </div>
            </div>
          </div>
        )}

        {needsConsent ? (
          <div className="space-y-4">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm text-muted-foreground space-y-2">
                {subject ? (
                  <>
                    {/* Written to be read ALOUD to the worker. The operator is
                        the one clicking, but they are not the one consenting,
                        and copy addressed to the clicker would record the wrong
                        person's agreement. */}
                    <p>
                      Read this to {subject.name} before you start:
                    </p>
                    <p>
                      &ldquo;Face check-in stores a mathematical summary of your face — not a
                      photo. Photos are never saved. It is used only to confirm it is you
                      when you clock in at this kiosk, and you can delete it yourself at
                      any time from your profile.&rdquo;
                    </p>
                    <p>
                      {subject.name} will be told that you set this up. Only continue with
                      them standing at this camera.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Face check-in stores a mathematical summary of your face — not a
                      photo. Photos are never saved.
                    </p>
                    <p>
                      It is used only to confirm it is you when you clock in. You can
                      delete it at any time, and clock in the normal way instead.
                    </p>
                  </>
                )}
              </div>
            </div>
            {failure && <p className="text-sm text-destructive">{failure}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Not now</Button>
              <Button onClick={doConsent} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {subject ? `${subject.name} agrees — continue` : 'I agree — continue'}
              </Button>
            </div>
          </div>
        ) : cameraError ? (
          <div className="space-y-4">
            <p className="text-sm text-destructive">{cameraError}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Close</Button>
              <Button onClick={startCamera}>
                <RefreshCw className="mr-2 h-4 w-4" /> Try again
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-lg bg-black">
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full"
                // Mirrored: an un-mirrored preview makes people correct their
                // position the wrong way.
                style={{ transform: 'scaleX(-1)' }}
              />
              <div
                className={`pointer-events-none absolute inset-3 rounded-lg border-2 ${
                  frameOk ? 'border-emerald-400' : 'border-white/30'
                }`}
              />
            </div>
            <canvas ref={canvasRef} className="hidden" />

            <p className={`text-sm ${frameOk ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {hint}
            </p>

            {/* Which camera. Shown whenever there is a choice, and named in the
                hint once a few ticks in a row found no face — the one case
                where "check the lighting" is the wrong advice. */}
            {devices.length > 1 && (
              <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Camera</span>
                <select
                  aria-label="Camera"
                  value={activeDeviceId || ''}
                  onChange={(e) => e.target.value && switchCamera(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground"
                >
                  {devices.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>{cameraLabel(d, i)}</option>
                  ))}
                </select>
                {!frameOk && noFaceStreak.current >= 4 && (
                  <span className="text-amber-600" data-testid="face-no-face-camera-hint">
                    No face from {cameraLabel(devices.find((d) => d.deviceId === activeDeviceId), 0)} — try another camera.
                  </span>
                )}
              </label>
            )}

            {mode === 'enroll' && captured > 0 && (
              <p className="text-xs text-muted-foreground">
                Captured {captured} of {ENROLL_FRAMES}
              </p>
            )}
            {failure && <p className="text-sm text-destructive">{failure}</p>}

            <div className="flex items-center justify-between gap-2">
              {/* Cancel stays available throughout: a camera that cannot be
                  dismissed is the thing people object to about these systems. */}
              <Button variant="outline" onClick={close}>
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>

              {failure ? (
                <Button onClick={() => void retry()}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Try again
                </Button>
              ) : (
                <span className="flex items-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'enroll' ? 'Capturing…' : 'Checking…'}
                </span>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
